import React, { useEffect, useState } from 'react';
import axios from 'axios';

const Franchise = () => {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFranchiseId, setEditingFranchiseId] = useState(null);
  const [formData, setFormData] = useState({
    franchiseName: '',
    franchisePhone: '',
    franchiseEmail: '',
    password: '',
    createdDate: '',
    address: '',
    franchiseLogo: null,
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);

  useEffect(() => {
    fetchFranchises();
  }, []);

  const fetchFranchises = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      console.log("Attempting to fetch franchises from /api/franchises");
      const response = await axios.get('https://customersupport-jhy9.onrender.com/api/franchises', {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status < 500,
      });

      console.log("Response status:", response.status);
      console.log("Response data:", response.data);

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
        window.location.href = '/login';
        return;
      }

      if (response.status === 404) {
        console.warn("Franchises endpoint not found, setting empty array");
        setFranchises([]);
        setError('Franchises endpoint not available. Please contact administrator.');
        return;
      }

      if (response.status >= 400) {
        console.error("Server error:", response.status, response.data);
        setFranchises([]);
        setError(`Server error: ${response.status}. Please try again later.`);
        return;
      }

      // Ensure we always set an array, handle different response structures
      if (Array.isArray(response.data)) {
        setFranchises(response.data);
        setError(null);
      } else if (response.data && Array.isArray(response.data.franchises)) {
        setFranchises(response.data.franchises);
        setError(null);
      } else if (response.data && Array.isArray(response.data.data)) {
        setFranchises(response.data.data);
        setError(null);
      } else {
        console.warn("Unexpected response structure:", response.data);
        setFranchises([]);
        setError('Unexpected data format received from server.');
      }
    } catch (err) {
      console.error("Error fetching franchises:", err.response || err.message || err);
      setFranchises([]); // Ensure franchises is always an array even on error
      
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
        window.location.href = '/login';
      } else if (err.response?.status === 404) {
        setError('Franchises endpoint not found. Please contact administrator.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later or contact administrator.');
      } else {
        setError('Failed to fetch franchises. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file, maxWidth = 300, maxHeight = 300, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleInputChange = async (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('File size must be less than 5MB');
          e.target.value = '';
          return;
        }
        
        if (!file.type.startsWith('image/')) {
          alert('Please select a valid image file');
          e.target.value = '';
          return;
        }
        
        try {
          const compressedFile = await compressImage(file);
          setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: compressedFile,
          }));
          
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoPreview(reader.result);
          };
          reader.readAsDataURL(compressedFile);
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Error processing image. Please try another file.');
        }
      }
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleAddFranchise = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    // Validate required fields
    if (!formData.franchiseName.trim()) {
      alert('Franchise name is required.');
      return;
    }
    if (!formData.franchisePhone.trim()) {
      alert('Franchise phone is required.');
      return;
    }
    if (!formData.franchiseEmail.trim()) {
      alert('Franchise email is required.');
      return;
    }
    if (!formData.password.trim()) {
      alert('Password is required.');
      return;
    }

    try {
      // If there's a logo file, convert it to base64
      let logoBase64 = null;
      if (formData.franchiseLogo) {
        const reader = new FileReader();
        logoBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const result = reader.result;
            if (result.length > 700000) {
              alert('Image is still too large after compression. Please choose a smaller image.');
              resolve(null);
            } else {
              resolve(result);
            }
          };
          reader.readAsDataURL(formData.franchiseLogo);
        });
        
        if (!logoBase64) {
          return;
        }
      }

      // Clean phone number
      let cleanPhone = formData.franchisePhone.trim();
      if (!cleanPhone.startsWith('+')) {
        cleanPhone = '+' + cleanPhone.replace(/\D/g, '');
      }

      const payload = {
        franchiseName: formData.franchiseName.trim(),
        franchisePhone: cleanPhone,
        franchiseEmail: formData.franchiseEmail.toLowerCase().trim(),
        password: formData.password.trim(),
        createdDate: formData.createdDate || new Date().toISOString(),
        ...(formData.address && formData.address.trim() && { address: formData.address.trim() }),
        ...(logoBase64 && { franchiseLogo: logoBase64 }),
      };

      console.log("Sending create franchise request");

      const response = await axios.post(
        'https://customersupport-jhy9.onrender.com/api/franchises',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          validateStatus: (status) => status < 500,
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
        window.location.href = '/login';
        return;
      }

      if (response.status === 200 || response.status === 201) {
        console.log("Franchise created successfully:", response.data);
        fetchFranchises();
        resetForm();
        alert('Franchise created successfully!');
      } else {
        console.error("Create failed with status:", response.status, response.data);
        if (response.status === 413) {
          alert('Request payload too large. Please use a smaller logo image.');
        } else if (response.status === 400) {
          alert(`Validation error: ${response.data?.message || 'Please check your input data.'}`);
        } else {
          alert('Error creating franchise. Please try again.');
        }
      }
    } catch (error) {
      console.error("Error creating franchise:", error.response?.data || error.message || error);
      alert('Error creating franchise. Please try again.');
    }
  };

  const handleEditFranchise = (franchise) => {
    setIsEditing(true);
    setEditingFranchiseId(franchise._id);
    setFormData({
      franchiseName: franchise.franchiseName || '',
      franchisePhone: franchise.franchisePhone || '',
      franchiseEmail: franchise.franchiseEmail || '',
      password: '',
      createdDate: franchise.createdDate
        ? new Date(franchise.createdDate).toISOString().split('T')[0]
        : '',
      address: franchise.address || '',
      franchiseLogo: null,
    });
    
    if (franchise.franchiseLogo) {
      setLogoPreview(franchise.franchiseLogo);
    } else {
      setLogoPreview(null);
    }
    
    setShowForm(true);
  };

  const handleUpdateFranchise = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    if (!formData.franchiseName.trim()) {
      alert('Franchise name is required.');
      return;
    }
    if (!formData.franchisePhone.trim()) {
      alert('Franchise phone is required.');
      return;
    }
    if (!formData.franchiseEmail.trim()) {
      alert('Franchise email is required.');
      return;
    }

    try {
      let logoBase64 = null;
      if (formData.franchiseLogo) {
        const reader = new FileReader();
        logoBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const result = reader.result;
            if (result.length > 700000) {
              alert('Image is still too large after compression. Please choose a smaller image.');
              resolve(null);
            } else {
              resolve(result);
            }
          };
          reader.readAsDataURL(formData.franchiseLogo);
        });
        
        if (logoBase64 === null && formData.franchiseLogo) {
          return;
        }
      }

      let cleanPhone = formData.franchisePhone.trim();
      if (!cleanPhone.startsWith('+')) {
        cleanPhone = '+' + cleanPhone.replace(/\D/g, '');
      }

      const payload = {
        franchiseName: formData.franchiseName.trim(),
        franchisePhone: cleanPhone,
        franchiseEmail: formData.franchiseEmail.toLowerCase().trim(),
        ...(formData.password && formData.password.trim() && { password: formData.password.trim() }),
        ...(formData.createdDate && { createdDate: new Date(formData.createdDate).toISOString() }),
        ...(formData.address && formData.address.trim() && { address: formData.address.trim() }),
        ...(logoBase64 && { franchiseLogo: logoBase64 }),
      };

      const response = await axios.put(
        `https://customersupport-jhy9.onrender.com/api/franchises/${editingFranchiseId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          validateStatus: (status) => status < 500,
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
        window.location.href = '/login';
        return;
      }

      if (response.status === 200 || response.status === 201) {
        console.log("Franchise updated successfully:", response.data);
        fetchFranchises();
        resetForm();
        alert('Franchise updated successfully!');
      } else {
        console.error("Update failed with status:", response.status, response.data);
        alert('Error updating franchise. Please try again.');
      }
    } catch (error) {
      console.error("Error updating franchise:", error.response?.data || error.message || error);
      alert('Error updating franchise. Please try again.');
    }
  };

  const handleDeleteFranchise = async (franchiseId) => {
    if (!window.confirm('Are you sure you want to delete this franchise? This action cannot be undone.')) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    try {
      const response = await axios.delete(
        `https://customersupport-jhy9.onrender.com/api/franchises/${franchiseId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500,
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        alert('Session expired. Please log in again.');
        window.location.href = '/login';
        return;
      }

      if (response.status === 200 || response.status === 204) {
        console.log("Franchise deleted successfully");
        fetchFranchises();
        alert('Franchise deleted successfully!');
      } else {
        console.error("Delete failed with status:", response.status, response.data);
        alert('Error deleting franchise. Please try again.');
      }
    } catch (error) {
      console.error("Error deleting franchise:", error.response?.data || error.message || error);
      alert('Error deleting franchise. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      franchiseName: '',
      franchisePhone: '',
      franchiseEmail: '',
      password: '',
      createdDate: '',
      address: '',
      franchiseLogo: null,
    });
    setLogoPreview(null);
    setShowForm(false);
    setIsEditing(false);
    setEditingFranchiseId(null);
  };

  const handleViewFranchise = (franchise) => {
    setSelectedFranchise(franchise);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedFranchise(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading franchises...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Franchise Management</h1>
            <p className="text-gray-600 mt-2">Manage all franchises in your system</p>
          </div>
          <button
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
            className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            {showForm ? 'Cancel' : 'Add Franchise'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? 'Edit Franchise' : 'Add New Franchise'}</h2>
            <form onSubmit={isEditing ? handleUpdateFranchise : handleAddFranchise} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Name *</label>
                  <input
                    type="text"
                    name="franchiseName"
                    value={formData.franchiseName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter franchise name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Phone *</label>
                  <input
                    type="text"
                    name="franchisePhone"
                    value={formData.franchisePhone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Email *</label>
                  <input
                    type="email"
                    name="franchiseEmail"
                    value={formData.franchiseEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="franchise@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password {isEditing ? '' : '*'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={isEditing ? 'Enter new password (optional)' : 'Set a password'}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <input
                    type="date"
                    name="createdDate"
                    value={formData.createdDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Franchise address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Logo</label>
                  <input
                    type="file"
                    name="franchiseLogo"
                    onChange={handleInputChange}
                    accept="image/*"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  {logoPreview && (
                    <div className="mt-2">
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="h-20 w-20 object-cover rounded-md border border-gray-300"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  {isEditing ? 'Update Franchise' : 'Create Franchise'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Franchise List</h2>
            <p className="text-gray-600 text-sm">{Array.isArray(franchises) ? franchises.length : 0} franchises found</p>
          </div>

          {!Array.isArray(franchises) || franchises.length === 0 ? (
            <div className="p-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m2-0h-2m0-3h.01M17 18h.01M14 18h.01" />
              </svg>
              <p className="mt-4 text-gray-600">No franchises found. Add your first franchise to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Franchise Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(franchises) && franchises.map((franchise, index) => (
                    <tr key={franchise._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {franchise.franchiseLogo ? (
                          <img
                            src={franchise.franchiseLogo}
                            alt="Franchise Logo"
                            className="h-10 w-10 object-cover rounded-full border border-gray-200"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0h-2m0-3h.01M17 18h.01M14 18h.01" />
                            </svg>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{franchise.franchiseName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{franchise.franchiseEmail}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{franchise.franchisePhone || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{franchise.address || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {franchise.createdDate ? new Date(franchise.createdDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleViewFranchise(franchise)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleEditFranchise(franchise)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteFranchise(franchise._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Franchise Modal */}
        {showViewModal && selectedFranchise && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800">Franchise Details</h2>
                <button
                  onClick={closeViewModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Franchise Logo Section */}
                  <div className="lg:col-span-1">
                    <div className="text-center">
                      <div className="mb-4">
                        {selectedFranchise.franchiseLogo ? (
                          <img
                            src={selectedFranchise.franchiseLogo}
                            alt="Franchise Logo"
                            className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-gray-200 shadow-lg"
                          />
                        ) : (
                          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mx-auto flex items-center justify-center border-4 border-gray-200 shadow-lg">
                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0h-2m0-3h.01M17 18h.01M14 18h.01" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedFranchise.franchiseName}</h3>
                      <p className="text-gray-600">{selectedFranchise.franchiseEmail}</p>
                    </div>
                  </div>

                  {/* Franchise Information */}
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Basic Information */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Basic Information</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Franchise Name</label>
                          <p className="text-gray-800 font-medium">{selectedFranchise.franchiseName}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Franchise Email</label>
                          <p className="text-gray-800">{selectedFranchise.franchiseEmail}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Franchise Phone</label>
                          <p className="text-gray-800">{selectedFranchise.franchisePhone || 'Not provided'}</p>
                        </div>
                      </div>

                      {/* Additional Information */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Additional Information</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
                          <p className="text-gray-800">
                            {selectedFranchise.createdDate 
                              ? new Date(selectedFranchise.createdDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'Not specified'
                            }
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                          <p className="text-gray-800">{selectedFranchise.address || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Franchise ID */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Franchise ID</label>
                        <p className="text-gray-600 font-mono text-sm bg-gray-50 px-3 py-2 rounded-md">{selectedFranchise._id}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    closeViewModal();
                    handleEditFranchise(selectedFranchise);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Edit Franchise
                </button>
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Franchise;
